/**
 * useNotificationPreferences Hook
 *
 * React Query hook for fetching and updating user notification channel preferences.
 * Implements optimistic updates with error rollback and cache invalidation.
 *
 * Features:
 * - Fetches preferences from GET /api/notifications/preferences via useQuery
 * - Mutation with optimistic update for instant UI feedback
 * - Error rollback reverts toggle state on API failure
 * - isMutating flag to disable toggles during in-flight requests
 * - Cache invalidation on settled to ensure fresh data
 *
 * Requirements: 8.3, 8.4, 9.1, 9.2, 9.3
 *
 * @module hooks/useNotificationPreferences
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import {
    NotificationPreferencesService,
    type NotificationPreferences,
    type NotificationPreferencesUpdate,
} from '../services/notification-preferences-service';

/** Query key for notification preferences cache management. */
export const NOTIFICATION_PREFERENCES_QUERY_KEY = ['notification-preferences'] as const;

/** Shape returned by the useNotificationPreferences hook. */
export interface UseNotificationPreferencesReturn {
    /** Current notification preferences (undefined while loading). */
    preferences: NotificationPreferences | undefined;
    /** Whether the initial fetch is in progress. */
    isLoading: boolean;
    /** Error from the fetch query, if any. */
    error: Error | null;
    /** Sends a PUT request with optimistic update. */
    updatePreference: (prefs: NotificationPreferencesUpdate) => void;
    /** Whether a mutation is currently in-flight (use to disable toggles). */
    isMutating: boolean;
}

/**
 * Hook for fetching and updating notification preferences with optimistic updates.
 *
 * Uses React Query for caching, automatic refetch on window focus,
 * and optimistic updates with error rollback for preference mutations.
 *
 * @example
 * ```tsx
 * const { preferences, isLoading, error, updatePreference, isMutating } = useNotificationPreferences();
 *
 * // Toggle in-app notifications
 * updatePreference({ in_app_enabled: !preferences?.in_app_enabled });
 * ```
 */
export function useNotificationPreferences(): UseNotificationPreferencesReturn {
    const { isAuthenticated } = useAuth();
    const queryClient = useQueryClient();

    // --- Query: fetch preferences ---
    const {
        data: preferences,
        isLoading,
        error,
    } = useQuery<NotificationPreferences, Error>({
        queryKey: NOTIFICATION_PREFERENCES_QUERY_KEY,
        queryFn: async (): Promise<NotificationPreferences> => {
            const service = new NotificationPreferencesService();
            return service.getPreferences();
        },
        enabled: isAuthenticated,
    });

    // --- Mutation: update preferences with optimistic update ---
    const mutation = useMutation<
        NotificationPreferences,
        Error,
        NotificationPreferencesUpdate,
        { previous: NotificationPreferences | undefined }
    >({
        mutationFn: async (prefs: NotificationPreferencesUpdate): Promise<NotificationPreferences> => {
            const service = new NotificationPreferencesService();
            return service.updatePreferences(prefs);
        },
        onMutate: async (newPrefs: NotificationPreferencesUpdate) => {
            // Cancel outgoing refetches to avoid overwriting optimistic update
            await queryClient.cancelQueries({ queryKey: NOTIFICATION_PREFERENCES_QUERY_KEY });

            // Snapshot current data for rollback
            const previous = queryClient.getQueryData<NotificationPreferences>(
                NOTIFICATION_PREFERENCES_QUERY_KEY,
            );

            // Optimistic update: merge new prefs into current state
            if (previous) {
                queryClient.setQueryData<NotificationPreferences>(
                    NOTIFICATION_PREFERENCES_QUERY_KEY,
                    {
                        ...previous,
                        ...newPrefs,
                    } as NotificationPreferences,
                );
            }

            return { previous };
        },
        onError: (_err, _newPrefs, context) => {
            // Rollback to previous state on failure
            if (context?.previous) {
                queryClient.setQueryData(
                    NOTIFICATION_PREFERENCES_QUERY_KEY,
                    context.previous,
                );
            }
        },
        onSettled: () => {
            // Invalidate to refetch fresh data from server
            queryClient.invalidateQueries({ queryKey: NOTIFICATION_PREFERENCES_QUERY_KEY });
        },
    });

    return {
        preferences,
        isLoading,
        error: error ?? null,
        updatePreference: mutation.mutate,
        isMutating: mutation.isPending,
    };
}
